import { CAREERS_LINK } from '@lib/constants/common'
import { LinguiJSMessageDescriptor, UnionizeObjKeys } from '@lib/types/common'
import {
  Discord,
  Facebook,
  Instagram,
  LinkedIn,
  Threads,
  Twitter,
  YCombinator,
  Youtube,
} from '@components/common/icons/custom-icons'
import { defineMessage } from '@lingui/macro'

export type ResourceLinkType = {
  title: LinguiJSMessageDescriptor
  link: string
  imagePath: string
}

export const RESOURCE_LINKS: ResourceLinkType[] = [
  {
    title: defineMessage({
      id: 'footer.resources.codingCompetitions',
      message: 'Coding Competitions',
    }),
    link: 'https://www.codingal.com/competitions/',
    imagePath: 'competitions.png',
  },
  {
    title: defineMessage({
      id: 'footer.resources.codingQuizzes',
      message: 'Coding Quizzes',
    }),
    link: 'https://www.codingal.com/quizzes/',
    imagePath: 'quizzes.png',
  },
  {
    title: defineMessage({
      id: 'footer.resources.codingMasterClasses',
      message: 'Coding Masterclasses',
    }),
    link: 'https://www.codingal.com/coding-for-kids/masterclasses/',
    imagePath: 'masterclass.png',
  },
  {
    title: defineMessage({
      id: 'footer.resources.codingWebinar',
      message: 'Coding Webinars',
    }),
    link: 'https://www.codingal.com/coding-for-kids/webinars/',
    imagePath: 'webinars.png',
  },
  {
    title: defineMessage({
      id: 'footer.resources.codingWorksheets',
      message: 'Coding Worksheets',
    }),
    link: 'https://www.codingal.com/coding-for-kids/diy-worksheets/',
    imagePath: 'worksheets.png',
  },
  {
    title: defineMessage({
      id: 'footer.resources.codingBlog',
      message: 'Coding Blog',
    }),
    link: 'https://www.codingal.com/coding-for-kids/blog/',
    imagePath: 'blog.png',
  },
  {
    title: defineMessage({
      id: 'footer.resources.codingGuides',
      message: 'Coding Guides',
    }),
    link: 'https://www.codingal.com/coding-for-kids/coding-guides/',
    imagePath: 'guide.png',
  },
  {
    title: defineMessage({
      id: 'footer.resources.codingCourses',
      message: 'Coding Courses',
    }),
    link: 'https://www.codingal.com/courses/',
    imagePath: 'courses.png',
  },
]

export type InternalLinksType = {
  icon?: JSX.Element
  label: string | LinguiJSMessageDescriptor
  link: string
}

export const INTERNAL_LINKS_CATEGORY = {
  SOCIAL: 'SOCIAL',
  TRENDING_ARTICLE: 'TRENDING_ARTICLE',
  COMPANY: 'COMPANY',
  BESTSELLING_COURSE: 'BESTSELLING_COURSE',
  VISUAL_PROGRAMMING: 'VISUAL_PROGRAMMING',
  CORE: 'CORE',
  WEB_DEV_LIB: 'WEB_DEV_LIB',
  CODING_TOOL: 'CODING_TOOL',
  APP_DEV_TOOL: 'APP_DEV_TOOL',
  VERSION_CONTROL: 'VERSION_CONTROL',
  AI_PLATFORM: 'AI_PLATFORM',
  PYTHON_LIBRARY: 'PYTHON_LIBRARY',
  LEARNING_PATH: 'LEARNING_PATH',
  RESOURCES_PARENT: 'RESOURCES_PARENT',
  FREE_TRIAL_CLASS: 'FREE_TRIAL_CLASS',
  PREMIUM_COURSE: 'PREMIUM_COURSE',
  CAMP_COURSE: 'CAMP_COURSE',
  CODING_FOR_KIDS: 'CODING_FOR_KIDS',
}

export type InternalLinksCategoryType = UnionizeObjKeys<
  typeof INTERNAL_LINKS_CATEGORY
>

export const INTERNAL_LINKS_CATEGORY_TITLES: Record<
  InternalLinksCategoryType,
  LinguiJSMessageDescriptor
> = {
  SOCIAL: defineMessage({ id: 'footer.followus', message: 'Follow Us On' }),
  TRENDING_ARTICLE: defineMessage({
    id: 'footer.trendingArticles',
    message: 'Trending Articles',
  }),
  COMPANY: defineMessage({ id: 'footer.Company', message: 'Company' }),
  BESTSELLING_COURSE: defineMessage({
    id: 'footer.bestSellingCourses',
    message: 'Bestselling Courses',
  }),
  VISUAL_PROGRAMMING: defineMessage({
    id: 'Visual & Block-Based Programming Tools',
    message: 'Visual & Block-Based Programming Tools',
  }),
  CORE: defineMessage({
    id: 'Core Programming Languages',
    message: 'Core Programming Languages',
  }),
  WEB_DEV_LIB: defineMessage({
    id: 'Web Development Libraries & Frameworks',
    message: 'Web Development Libraries & Frameworks',
  }),
  CODING_TOOL: defineMessage({
    id: 'Coding Environments & Developer Tools',
    message: 'Coding Environments & Developer Tools',
  }),
  APP_DEV_TOOL: defineMessage({
    id: 'Mobile App Development Tools',
    message: 'Mobile App Development Tools',
  }),
  VERSION_CONTROL: defineMessage({
    id: 'Version Control & Collaboration Tools',
    message: 'Version Control & Collaboration Tools',
  }),
  AI_PLATFORM: defineMessage({
    id: 'AI & Machine Learning Platforms',
    message: 'AI & Machine Learning Platforms',
  }),
  PYTHON_LIBRARY: defineMessage({
    id: 'Python Libraries for Projects',
    message: 'Python Libraries for Projects',
  }),
  LEARNING_PATH: defineMessage({
    id: 'Learning Pathways',
    message: 'Learning Pathways',
  }),
  RESOURCES_PARENT: defineMessage({
    id: 'Free Resources for Parents',
    message: 'Free Resources for Parents',
  }),
  FREE_TRIAL_CLASS: defineMessage({
    id: 'Free Trial Lessons for Kids',
    message: 'Free Trial Lessons for Kids',
  }),
  PREMIUM_COURSE: defineMessage({
    id: 'Premium Courses',
    message: 'Premium Courses',
  }),
  CAMP_COURSE: defineMessage({
    id: 'Camp Courses',
    message: 'Camp Courses',
  }),
  CODING_FOR_KIDS: defineMessage({
    id: 'Coding for Kids by Grade',
    message: 'Coding for Kids by Grade',
  }),
}

export const INTERNAL_LINKS_DATA: Record<
  InternalLinksCategoryType,
  InternalLinksType[]
> = {
  SOCIAL: [
    {
      icon: <Facebook className="w-5" />,
      label: 'Facebook',
      link: 'https://www.facebook.com/codingal.education',
    },
    {
      icon: <Twitter className="w-5" />,
      label: 'X (Twitter)',
      link: 'https://x.com/codingal',
    },
    {
      icon: <Youtube className="w-5" />,
      label: 'Youtube',
      link: 'https://www.youtube.com/c/CodingalEducation',
    },
    {
      icon: <LinkedIn className="w-5" />,
      label: 'Linkedin',
      link: 'https://www.linkedin.com/company/codingal',
    },
    {
      icon: <Instagram className="w-5" />,
      label: 'Instagram',
      link: 'https://www.instagram.com/codingal.education/',
    },
    {
      icon: <Threads className="w-5" />,
      label: 'Threads',
      link: 'https://www.threads.com/@codingal.education',
    },
    {
      icon: <Discord className="w-5" />,
      label: 'Discord',
      link: 'https://discord.com/invite/qXrAWQKxmY',
    },
    {
      icon: <YCombinator className="w-5" />,
      label: 'Y Combinator',
      link: 'https://www.ycombinator.com/companies/codingal',
    },
  ],
  TRENDING_ARTICLE: [
    {
      label: defineMessage({
        id: 'Create Your First Roblox Game',
        message: 'Create Your First Roblox Game',
      }),
      link:
        'https://www.codingal.com/coding-for-kids/blog/steps-to-creating-your-first-roblox-game/',
    },
    {
      label: defineMessage({
        id: 'Python Projects for Beginners',
        message: 'Python Projects for Beginners',
      }),
      link:
        'https://www.codingal.com/coding-for-kids/blog/python-projects-beginners/',
    },
    {
      label: defineMessage({
        id: '2D Games with Python',
        message: '2D Games with Python',
      }),
      link:
        'https://www.codingal.com/coding-for-kids/blog/2d-games-with-python/',
    },
    {
      label: defineMessage({
        id: 'How to Create a Roblox Avatar',
        message: 'How to Create a Roblox Avatar',
      }),
      link:
        'https://www.codingal.com/coding-for-kids/blog/how-to-create-a-roblox-avatar/',
    },
    {
      label: defineMessage({
        id: 'Rock, Paper & Scissors with Python',
        message: 'Rock, Paper & Scissors with Python',
      }),
      link:
        'https://www.codingal.com/coding-for-kids/blog/rock-paper-scissors-game-in-python/',
    },
    {
      label: defineMessage({
        id: 'Build a Simple Quiz Game Using Scratch',
        message: 'Build a Simple Quiz Game Using Scratch',
      }),
      link:
        'https://www.codingal.com/coding-for-kids/blog/build-a-simple-quiz-game-using-scratch/',
    },
    {
      label: defineMessage({
        id: 'How to Make a Flappy Bird on Scratch',
        message: 'How to Make a Flappy Bird on Scratch',
      }),
      link:
        'https://www.codingal.com/coding-for-kids/blog/how-to-make-flappy-bird-on-scratch/',
    },
    {
      label: defineMessage({
        id: 'Building GUI Games on Python',
        message: 'Building GUI Games on Python',
      }),
      link:
        'https://www.codingal.com/coding-for-kids/blog/build-gui-games-in-python/',
    },
    {
      label: defineMessage({
        id: 'How to Create a Jumping Game on Scratch',
        message: 'How to Create a Jumping Game on Scratch',
      }),
      link:
        'https://www.codingal.com/coding-for-kids/blog/how-to-create-jumping-game-in-scratch/',
    },
    {
      label: defineMessage({
        id: 'How to Create Obby Game in Roblox',
        message: 'How to Create Obby Game in Roblox',
      }),
      link:
        'https://www.codingal.com/coding-for-kids/blog/how-to-create-obby-game-in-roblox/',
    },
  ],
  COMPANY: [
    {
      label: defineMessage({ message: 'About us' }),
      link: 'https://www.codingal.com/about-us/',
    },
    {
      label: defineMessage({ message: 'Reviews and testimonials' }),
      link: 'https://www.codingal.com/reviews-and-testimonials/',
    },
    {
      label: defineMessage({ message: 'Curriculum' }),
      link: 'https://www.codingal.com/curriculum/',
    },
    {
      label: defineMessage({ message: 'Careers' }),
      link: CAREERS_LINK,
    },
    {
      label: defineMessage({ message: 'School Partnerships' }),
      link: 'https://www.codingal.com/school-partnership/',
    },
    {
      label: defineMessage({ message: 'Press releases' }),
      link: 'https://www.codingal.com/press/',
    },
    {
      label: defineMessage({ message: 'Become a teacher' }),
      link: 'https://www.codingal.com/teach/',
    },
    {
      label: defineMessage({ message: 'Run System check' }),
      link: 'https://www.codingal.com/system-check/',
    },
    {
      label: defineMessage({ message: 'Browse Locations' }),
      link: 'https://www.codingal.com/locations/',
    },
    {
      label: defineMessage({ message: 'Codingal Wrapped' }),
      link: 'https://www.codingal.com/wrapped/',
    },
  ],
  BESTSELLING_COURSE: [
    {
      label: defineMessage({ message: `AI and Machine Learning` }),
      link: '/courses/ai-and-machine-learning/',
    },
    {
      label: defineMessage({ message: `Scratch Programming with AI` }),
      link: '/courses/scratch-programming/',
    },
    {
      label: defineMessage({ message: `Python Champion` }),
      link: '/courses/python-for-kids/',
    },
    {
      label: defineMessage({ message: `Game Development for Kids` }),
      link: '/courses/game-development/',
    },
    {
      label: defineMessage({ message: `Website Development` }),
      link: '/courses/web-development/',
    },
    {
      label: defineMessage({ message: `App Development for Kids` }),
      link: '/courses/app-development/',
    },
    {
      label: defineMessage({ message: `AI & Data Science for Teens` }),
      link: '/courses/data-science/',
    },
    {
      label: defineMessage({ message: `Roblox Champion` }),
      link: '/courses/roblox-coding/',
    },
    {
      label: defineMessage({ message: `AP Computer Science A` }),
      link: '/courses/ap-computer-science-a/',
    },
    {
      label: defineMessage({ message: `IOI Algorithms` }),
      link: '/courses/ioi-algorithms-course/',
    },
    {
      label: defineMessage({ message: `Math` }),
      link: '/courses/math/',
    },
    {
      label: defineMessage({ message: `Public Speaking for Kids` }),
      link: '/courses/public-speaking-for-kids/',
    },
    {
      label: defineMessage({ message: `Creative Writing for Kids` }),
      link: '/courses/creative-writing-for-kids/',
    },
  ],
  VISUAL_PROGRAMMING: [
    {
      label: defineMessage({ message: 'Animation' }),
      link: '/courses/animation/',
    },
    {
      label: defineMessage({ message: 'Code.org' }),
      link: '/courses/code-org-courses-for-kids/',
    },
    {
      label: defineMessage({ message: 'Pictoblox' }),
      link: '/courses/pictoblox/',
    },
    {
      label: defineMessage({ message: 'MIT App Inventor' }),
      link: '/courses/mit-app-inventor-for-kids/',
    },
    {
      label: defineMessage({ message: 'Teachable Machine' }),
      link: '/courses/teachable-machine/',
    },
  ],
  CORE: [
    {
      label: defineMessage({ message: 'JavaScript' }),
      link: '/courses/learn-javascript-for-kids-and-teens/',
    },
    {
      label: defineMessage({ message: 'Java' }),
      link: '/courses/java/',
    },
    {
      label: defineMessage({ message: 'SQL' }),
      link: '/courses/sql/',
    },
  ],
  WEB_DEV_LIB: [
    {
      label: defineMessage({ message: 'Node.js' }),
      link: '/courses/node-js/',
    },
    {
      label: defineMessage({ message: 'React.js' }),
      link: '/courses/reactjs-for-kids/',
    },
    {
      label: defineMessage({ message: 'HTML' }),
      link: '/courses/html-for-kids/',
    },
    {
      label: defineMessage({ message: 'CSS' }),
      link: '/courses/css-for-kids/',
    },
    {
      label: defineMessage({ message: 'Bootstrap' }),
      link: '/courses/bootstrap-web-design-for-kids/',
    },
  ],
  CODING_TOOL: [
    {
      label: defineMessage({ message: 'Google Colab' }),
      link: '/courses/google-colab-for-kids/',
    },
    {
      label: defineMessage({ message: 'Trinket' }),
      link: '/courses/trinket/',
    },
    {
      label: defineMessage({ message: 'VS Code' }),
      link: '/courses/visual-studio-code/',
    },
  ],
  APP_DEV_TOOL: [
    {
      label: defineMessage({ message: 'Thunkable' }),
      link: '/courses/thunkable/',
    },
    {
      label: defineMessage({ message: 'MIT App Inventor' }),
      link: '/courses/mit-app-inventor-for-kids/',
    },
  ],
  VERSION_CONTROL: [
    {
      label: defineMessage({ message: 'Git' }),
      link: '/courses/git-for-kids/',
    },
    {
      label: defineMessage({ message: 'Github' }),
      link: '/courses/github-for-kids/',
    },
  ],
  AI_PLATFORM: [
    {
      label: defineMessage({ message: 'AI Code 101' }),
      link: '/courses/ai-code-101-for-kids/',
    },
  ],
  PYTHON_LIBRARY: [
    {
      label: defineMessage({ message: 'Pygame' }),
      link: '/courses/pygame/',
    },
    {
      label: defineMessage({ message: 'Turtle' }),
      link: '/courses/turtle-graphics-for-kids/',
    },
  ],
  LEARNING_PATH: [
    {
      label: defineMessage({ message: 'Coding for Elementary' }),
      link: 'https://www.codingal.com/curriculum/elementary-school/',
    },
    {
      label: defineMessage({ message: 'Coding for Middle School' }),
      link: 'https://www.codingal.com/curriculum/middle-school/',
    },
    {
      label: defineMessage({ message: 'Coding for High School' }),
      link: 'https://www.codingal.com/curriculum/high-school/',
    },
    {
      label: defineMessage({ message: 'Coding for Teens' }),
      link: 'https://www.codingal.com/coding-for-teens/',
    },
    {
      label: defineMessage({ message: 'Coding for girls' }),
      link: 'https://www.codingal.com/coding-for-girls/',
    },
    {
      label: defineMessage({ message: 'Coding for Boys' }),
      link: 'https://www.codingal.com/coding-for-boys/',
    },
    {
      label: defineMessage({ message: 'Benefits of coding' }),
      link: 'https://www.codingal.com/benefits-of-coding/',
    },
    {
      label: defineMessage({ message: 'Why should kids learn to code' }),
      link: 'https://www.codingal.com/why-should-kids-learn-to-code/',
    },
    {
      label: defineMessage({ message: 'Is learning coding worth it' }),
      link: 'https://www.codingal.com/is-learning-coding-worth-it/',
    },
  ],
  RESOURCES_PARENT: [],
  FREE_TRIAL_CLASS: [],
  PREMIUM_COURSE: [
    {
      label: defineMessage({ message: 'AI & Coding Grandmaster' }),
      link: 'https://www.codingal.com/courses/coding-grandmaster/',
    },
    {
      label: defineMessage({ message: 'AI & Coding Champion' }),
      link: 'https://www.codingal.com/courses/coding-champion/',
    },
    {
      label: defineMessage({ message: 'AI & Coding Prodigy' }),
      link: 'https://www.codingal.com/courses/coding-prodigy/',
    },
  ],
  CAMP_COURSE: [
    {
      label: defineMessage({ message: 'Summer coding camp' }),
      link: '/kids-teens-coding-camps/summer/',
    },
    {
      label: defineMessage({ message: 'Winter coding camp' }),
      link: '/kids-teens-coding-camps/winter/',
    },
    {
      label: defineMessage({ message: 'Spring break coding camp' }),
      link: '/kids-teens-coding-camps/spring-break/',
    },
    {
      label: defineMessage({ message: 'Thanksgiving coding camp' }),
      link: '/kids-teens-coding-camps/thanksgiving/',
    },
    {
      label: defineMessage({ message: 'Black Friday coding camp' }),
      link: '/kids-teens-coding-camps/black-friday/',
    },
  ],
  CODING_FOR_KIDS: [
    {
      label: defineMessage({ message: 'Coding for kids in Grades 1-3' }),
      link: '/coding-for-kids/grade-1-3/',
    },
    {
      label: defineMessage({ message: 'Coding for kids in Grades 4-5' }),
      link: '/coding-for-kids/grade-4-5/',
    },
    {
      label: defineMessage({ message: 'Coding for kids in Grades 6-8' }),
      link: '/coding-for-kids/grade-6-8/',
    },
    {
      label: defineMessage({ message: 'Coding for kids in Grades 9-10' }),
      link: '/coding-for-kids/grade-9-10/',
    },
    {
      label: defineMessage({ message: 'Coding for kids in Grades 11-12' }),
      link: '/coding-for-kids/grade-11-12/',
    },
  ],
}
